import { FieldErrors, useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '@ramsey-design-system/form-field';
import { useGAFormTracking } from '../../../shared/hooks';
import { useMatcherContext } from '../context';
import { getAnsweredString } from '../flow/types';
import { matcherPhoneSchema } from '../schemas/MatcherPhoneSchema';
import { useWizard } from 'react-use-wizard';
import { startMatcherVerification } from '../api';
import Button from '@ramsey-design-system/button';
import { ChatIcon, PhoneIcon } from '@ramsey-design-system/icons';
import { PatternFormat } from 'react-number-format';
import Input from '@ramsey-design-system/input';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import {
  getMatcherFormName,
  useMatcherButtonSize,
  useMatcherFieldSize,
  useTrackMatcherStepStarted
} from '../utils';
import {
  MatcherStandardStepSubtitleText,
  MatcherStandardStepTitle,
  MatcherStepWrapper,
  MatcherPhoneVerificationCountDownAlert,
  MatcherErrorMessage,
  MatcherRamseyAgreementText
} from '../components';

const STEP_NAME = 'matcher_phone_step';
const STEP_LABEL = "What's your phone number?";

interface MatcherPhoneStepFields {
  phone: string;
}

function MatcherPhoneStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const { nextStep, activeStep } = useWizard();
  const [errorMessage, setErrorMessage] = useState('');
  const verificationType = useRef<'call' | 'sms' | ''>('');
  const [isLoadingSms, setIsLoadingSms] = useState(false);
  const [isLoadingCall, setIsLoadingCall] = useState(false);
  // Set synchronously, unlike the isLoading flags, which only land after react-hook-form's async
  // validation resolves and a re-render - the buttons are still enabled in that gap.
  const requestInFlight = useRef(false);
  const {
    matcherAnswers,
    updateMatcherAnswer,
    phoneVerificationCountDown,
    updatePhoneVerificationCountDown
  } = useMatcherContext();
  const fieldSize = useMatcherFieldSize();
  const buttonSize = useMatcherButtonSize();
  const postalCode = getAnsweredString(matcherAnswers.postalCode);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<MatcherPhoneStepFields>({
    defaultValues: {
      phone: getAnsweredString(matcherAnswers.phone)
    },
    resolver: yupResolver(matcherPhoneSchema)
  });

  useTrackMatcherStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(values: MatcherPhoneStepFields) {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    try {
      const tempVerificationType =
        verificationType.current === 'call' ? 'call' : 'sms';
      setErrorMessage('');
      const isLoading =
        tempVerificationType === 'sms' ? setIsLoadingSms : setIsLoadingCall;
      isLoading(true);
      const phone = values.phone.replaceAll(/\D/g, '');
      updateMatcherAnswer('phone', { status: 'answered', value: phone });
      updateMatcherAnswer('verificationType', {
        status: 'answered',
        value: tempVerificationType
      });
      await startMatcherVerification(phone, tempVerificationType, postalCode);
      trackStepCompleted({
        step: activeStep + 1,
        step_name: STEP_NAME,
        step_label: STEP_LABEL,
        answer_labels: ['Phone'],
        answer_values: [phone],
        form_name: getMatcherFormName()
      });
      updatePhoneVerificationCountDown(30);
      await nextStep();
    } catch (e) {
      const errorMessage =
        'Sorry, we couldn’t connect you with a pro. Try again.';
      setErrorMessage(errorMessage);
      trackStepErrorViewed({
        step: activeStep + 1,
        step_name: STEP_NAME,
        step_label: STEP_LABEL,
        error_type: 'system',
        error_message: errorMessage,
        form_name: getMatcherFormName()
      });
      console.error(e);
    } finally {
      verificationType.current = '';
      requestInFlight.current = false;
      setIsLoadingCall(false);
      setIsLoadingSms(false);
    }
  }

  function onInvalidSubmit(errors: FieldErrors<MatcherPhoneStepFields>) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      error_type: 'validation',
      error_field: 'phone',
      error_message: errors.phone?.message ?? '',
      form_name: getMatcherFormName()
    });
  }

  function setCallVerification() {
    verificationType.current = 'call';
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  function setTextingVerification() {
    verificationType.current = 'sms';
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  return (
    <MatcherStepWrapper>
      <form
        ref={formRef}
        // eslint-disable-next-line react-hooks/refs
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle>
          What's your phone number?
        </MatcherStandardStepTitle>
        <MatcherStandardStepSubtitleText>
          We’ll send you a six-digit code.
        </MatcherStandardStepSubtitleText>
        <div className="space-y-2 mb-4">
          <FormField
            id="phone"
            label="Phone"
            size={fieldSize}
            invalid={!!errors.phone}
            errors={[errors.phone?.message]}
          >
            {() => (
              <PatternFormat
                {...register('phone')}
                type="tel"
                id="phone"
                autoComplete="tel-national"
                defaultValue={getAnsweredString(matcherAnswers.phone)}
                size={fieldSize}
                invalid={!!errors.phone}
                data-testid="tax_matcher_phone_input"
                placeholder="(___) ___-____"
                format="(###) ###-####"
                mask="_"
                onValueChange={(value) => {
                  setValue('phone', value.formattedValue);
                }}
                customInput={Input}
              />
            )}
          </FormField>
        </div>

        {phoneVerificationCountDown > 0 && (
          <MatcherPhoneVerificationCountDownAlert
            phoneVerificationCountDown={phoneVerificationCountDown}
          />
        )}

        {errorMessage && <MatcherErrorMessage errorMessage={errorMessage} />}
        <MatcherRamseyAgreementText />
        <div className="flex flex-col gap-1 items-center">
          <Button
            type="submit"
            icon={ChatIcon}
            className="w-full!"
            fullWidthOnMobile={true}
            data-testid="tax_matcher_text_me_button"
            size={buttonSize}
            aria-label="Send verification code via text"
            loading={isLoadingSms}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setTextingVerification();
              }
            }}
            onClick={() => {
              setTextingVerification();
            }}
            disabled={
              phoneVerificationCountDown > 0 || isLoadingSms || isLoadingCall
            }
          >
            {isLoadingSms ? 'Texting . . .' : 'Text Me'}
          </Button>
          <Button
            type="submit"
            icon={PhoneIcon}
            className="w-full!"
            appearance="subtle"
            fullWidthOnMobile={true}
            data-testid="tax_matcher_call_me_button"
            size={buttonSize}
            aria-label="Send verification code via phone call"
            loading={isLoadingCall}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setCallVerification();
              }
            }}
            onClick={() => {
              setCallVerification();
            }}
            disabled={
              phoneVerificationCountDown > 0 || isLoadingSms || isLoadingCall
            }
          >
            {isLoadingCall ? 'Calling . . .' : 'Call Me'}
          </Button>
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherPhoneStep };
